export enum Severity {
  CRITICAL = 'Critical',
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low',
  INFO = 'Info'
}

export enum Category {
  ON_PAGE = 'On-Page SEO',
  TECHNICAL = 'Technical SEO',
  OFF_PAGE = 'Off-Page SEO',
  AEO = 'AEO (Answer Engine Opt.)'
}

export interface Issue {
  id: string;
  title: string;
  description: string;
  category: Category;
  severity: Severity;
  fix: string;
  impact: string;
  snippet?: string;
  priority?: 'P0' | 'P1' | 'P2' | 'P3'; // Priority matrix from spec
}

export interface CategoryScore {
  score: number;
  totalIssues: number;
  criticalIssues: number;
}

export interface BrokenLink {
  url: string;
  statusCode: number;
  statusText: string;
  foundIn: string;
  anchorText?: string;
}

export interface RedirectInfo {
  from: string;
  to: string;
  statusCode: number;
  redirectType: '301' | '302' | '307' | '308' | 'other';
  isCanonical: boolean;
}

export interface SitemapInfo {
  exists: boolean;
  url?: string;
  foundIn?: 'robots.txt' | 'meta' | 'common-location';
  accessible: boolean;
  error?: string;
}

export interface LinkAnalysis {
  brokenLinks: BrokenLink[];
  redirects: RedirectInfo[];
  sitemap: SitemapInfo;
  totalLinksChecked: number;
  internalLinks: number;
  externalLinks: number;
}

export interface PerformanceAuditResult {
  minification: {
    css: Array<{
      url: string;
      originalSize: string;
      minifiedSize: string;
      potentialSavings: number;
      potentialSavingsFormatted: string;
      isMinified: boolean;
      savingsPercentage: string;
      type: 'css' | 'js';
    }>;
    js: Array<{
      url: string;
      originalSize: string;
      minifiedSize: string;
      potentialSavings: number;
      potentialSavingsFormatted: string;
      isMinified: boolean;
      savingsPercentage: string;
      type: 'css' | 'js';
    }>;
    totalSavings: number;
    totalSavingsFormatted: string;
  };
  cacheability: {
    totalAssets: number;
    cached: number;
    uncached: number;
    details: Array<{
      url: string;
      isCached: boolean;
      cacheControl: string;
      maxAge: number;
      maxAgeFormatted: string;
      expires: string;
      etag: string;
      lastModified: string;
      recommendation: string;
      error?: string;
    }>;
  };
  images: {
    totalImages: number;
    properlySized: number;
    hasLazyLoad: number;
    hasDimensions: number;
    needsOptimization: number;
    notNextGen: number;
    details: Array<{
      url: string;
      format?: string;
      isNextGen: boolean;
      actualWidth?: number;
      actualHeight?: number;
      declaredWidth?: string;
      declaredHeight?: string;
      hasDimensions: boolean;
      properlySized: boolean;
      hasLazyLoad: boolean;
      currentSize: string;
      optimizedSize?: string;
      potentialSavings?: string;
      needsOptimization: boolean;
      savingsPercentage?: string;
      recommendations: string[];
      error?: string;
    }>;
  };
  overallScore: number;
  recommendations: Array<{
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    category: string;
    issue: string;
    action: string;
  }>;
}

export interface SeoReport {
  overallScore: number;
  url: string;
  summary: string;
  categories: {
    [key in Category]: CategoryScore;
  };
  issues: Issue[];
  keywordsDetected: string[];
  linkAnalysis?: LinkAnalysis; // New field for link analysis
  performanceAudit?: PerformanceAuditResult; // New field for performance audit
  enhanced?: any; // Enhanced SEO analysis data
}

export type AnalysisStatus = 'idle' | 'analyzing' | 'complete' | 'error';

export interface KeywordResult {
  keyword: string;
  intent: 'Informational' | 'Transactional' | 'Commercial' | 'Navigational';
  difficulty: 'Easy' | 'Medium' | 'Hard';
  volumeEstimate: string; 
  cpcEstimate: string; 
}

export interface CompetitorAnalysis {
  myUrl: string;
  competitorUrl: string;
  strongestFeatures?: {
    content?: string;
    technical?: string;
    keywords?: string;
    structure?: string;
    backlinks?: string;
    aeo?: string;
  };
  contentGap: string[];
  missingKeywords: string[];
  technicalAdvantages?: string[];
  strategy: string;
  detailedReport?: string;
  extractedData?: {
    basicInfo?: {
      title: string;
      titleLength: number;
      metaDescription: string;
      metaDescriptionLength: number;
      h1: string;
      h1Count: number;
      favicon: string | null;
      canonical: string | null;
      language: string;
      charset: string;
      viewport: string;
      ogImage: string | null;
      twitterCard: string | null;
    };
    contentAnalysis?: {
      wordCount: number;
      characterCount: number;
      paragraphCount: number;
      headingCounts: {
        h1: number;
        h2: number;
        h3: number;
        h4: number;
        h5: number;
        h6: number;
      };
      topKeywords: Array<{ word: string; count: number; percentage: string }>;
      images: {
        total: number;
        withAlt: number;
        withoutAlt: number;
      };
      links: {
        internal: number;
        external: number;
        total: number;
      };
      readabilityEstimate: {
        averageWordsPerSentence: string;
        level: string;
      };
    };
    technicalSEO?: {
      hasSSL: boolean;
      hasCanonical: boolean;
      hasViewport: boolean;
      hasRobotsMeta: boolean;
      robotsContent: string;
      structuredData: {
        count: number;
        types: string[];
      };
      openGraph: {
        hasTitle: boolean;
        hasDescription: boolean;
        hasImage: boolean;
        hasURL: boolean;
      };
      twitterCards: {
        hasCard: boolean;
        hasTitle: boolean;
        hasDescription: boolean;
        hasImage: boolean;
      };
      xmlSitemap: {
        exists: boolean;
        url?: string;
      };
      robotsTxt: {
        exists: boolean;
        url?: string;
      };
    };
    technologyStack?: {
      cms: string[];
      frameworks: string[];
      analytics: string[];
      advertising: string[];
      cdn: string[];
      hosting: string[];
      ecommerce: string[];
    };
    keywordStrategy?: any;
    contentStrategy?: any;
    competitiveAdvantages?: any;
    weaknesses?: any;
    recommendations?: any;
  };
}

export interface ContentIdea {
  topic: string;
  titleSuggestion: string;
  targetAudience: string;
  outline: string[];
}

// --- NEW SAAS TYPES ---

export type UserRole = 'user' | 'admin';
export type PlanType = 'free' | 'weekly' | 'monthly' | 'yearly';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  credits: number; // 10 for free trial
  plan: PlanType;
  subscriptionExpiry: string | null; // ISO Date
  isApproved?: boolean;
  emailVerified?: boolean;
  preferredLanguage?: string; // Language for analysis results
  uiLanguage?: string; // Language for website UI
  aiAssistantLanguage?: string; // Language for AI assistant responses
  preferredCountry?: string;
  whatsappNumber?: string;
  isKicked?: boolean;
  kickedAt?: string | null;
  kickReason?: string | null;
  createdAt?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
}